// Palworld progression-item keyword cluster: Starfruit, condensation star
// ranks, Bounty Tokens, stat fruits and Skill Fruit trees.
//
// These are high-intent Palworld wiki queries currently owned by palworld.gg,
// paldb.cc and palify.org. Each topic targets one query family no existing
// Pal Stars guide covers, and expands into five AEO-shaped articles
// (overview, step-by-step, reference, mistakes, FAQ) so every title stays
// unique and every answer stays extractable. Structure mirrors
// src/lib/guides-tcg-features-2026.ts.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-starfruit",
    title: "Palworld Starfruit",
    desc: "What Starfruits do in Palworld, how each ☆1-☆4 fruit differs from Ripe Starfruit, and the fastest way to bank enough to push a Pal to four stars without condensing duplicates.",
    keywords:
      "palworld starfruit, palworld star fruits, ripe starfruit palworld, palworld starfruit 1 2 3 4, how to get starfruit palworld, palworld starfruit vs condensing",
    hub: "/items",
    hubLabel: "Palworld item database",
    tag: "Items",
    question: "What does Starfruit do in Palworld?",
    answer:
      "A Starfruit raises a Pal's star rank by one step without condensing duplicates. The numbered fruits are locked to a single step — Starfruit ☆1 only takes a Pal from ☆0 to ☆1 — while Ripe Starfruit is flexible and raises a Pal by one rank from whatever rank it is already at, which makes it the most valuable of the set.",
    steps: [
      ["Decide which Pal actually deserves the fruit", "Star ranks add flat attack and defence and raise the level cap on work suitability, so spend fruits on the Pal you use daily — a mount, a boss-damage carry or a base worker you never swap out."],
      ["Check the Pal's current star rank first", "A numbered Starfruit is wasted if the Pal is already past that step. Open the Pal's status screen and read the stars before you consume anything."],
      ["Bank Ripe Starfruit for the expensive steps", "The later ranks cost the most duplicates to condense, so save flexible Ripe Starfruit for ☆3 and ☆4 and spend numbered fruits on the cheap early steps."],
      ["Trade tokens at the bounty/medal exchange", "Starfruits are exchange rewards rather than field loot in current builds — turn in the tokens you earn from bounty and challenge content instead of grinding random spawns for them."],
      ["Finish the rank with condensation if you run dry", "Fruits and condensing reach the same result. If you have surplus duplicates of a common Pal, condense that one and keep the fruits for the Pals you will never see duplicates of."],
    ],
    data: [
      ["Starfruit ☆1", "Epic consumable. Raises a Pal from ☆0 to ☆1 only. Weight 1, stacks to 9,999."],
      ["Starfruit ☆2 / ☆3 / ☆4", "Each is locked to its own single step, so a ☆4 fruit does nothing for a ☆1 Pal."],
      ["Ripe Starfruit", "Legendary consumable. Raises a Pal by one rank from any starting rank — the flexible, most valuable version."],
      ["Effect of a star rank", "Higher attack and defence, plus a raised cap on work suitability levels for base work."],
      ["Not merchant stock", "Starfruits are not sold by ordinary wandering merchants; they come from token exchange rewards, so availability tracks the current game version."],
    ],
    pitfalls: [
      ["Feeding a numbered fruit to the wrong rank", "A ☆2 fruit on an already-☆2 Pal is a dead item. Always read the star row on the status screen first."],
      ["Spending Ripe Starfruit early", "Using the flexible fruit on the ☆0-to-☆1 step wastes its only advantage. Save it for the steps that would otherwise cost the most duplicates."],
      ["Starring a Pal you will replace", "Star ranks do not transfer. Rank the Pal you actually field, not the first legendary you catch."],
      ["Assuming fruits beat condensing", "They are interchangeable in effect. Duplicates you already own are free; fruits are finite — use the cheaper resource for each step."],
    ],
    faq: [
      ["Can Starfruit take a Pal to four stars?", "Yes. Star ranks can be filled entirely with fruits, entirely with condensation, or any mix — the fruit simply substitutes for the duplicates that step would have needed."],
      ["Is Ripe Starfruit better than Starfruit ☆4?", "Ripe Starfruit is more useful because it applies to any rank. A ☆4 fruit is only usable on a Pal already sitting at ☆3."],
      ["Where do you get Starfruit in Palworld?", "From token exchange rewards tied to bounty and challenge content rather than from field drops or wandering merchants. Check the exchange stock in your current game version."],
      ["Do Starfruits affect breeding?", "No. Star rank is not inherited — a bred child hatches at ☆0 regardless of its parents' ranks, so fruits are an endgame investment, not a breeding shortcut."],
      ["Do Starfruits raise work suitability?", "Indirectly. A higher star rank raises the ceiling for work suitability levels, which is why base-worker Pals benefit from stars as much as combat Pals do."],
    ],
  },
  {
    slug: "palworld-condensation-star-ranks",
    title: "Palworld Condensation & Star Ranks",
    desc: "How Palworld condensation works, how many duplicates each star rank costs, and when a Starfruit is cheaper than feeding the Pal Essence Condenser.",
    keywords:
      "palworld condensation, palworld star ranks, pal essence condenser, how many pals to condense, palworld 4 star condensation, palworld condensing guide",
    hub: "/pals",
    hubLabel: "Palworld Pal database",
    tag: "Progression",
    question: "How does condensation work in Palworld?",
    answer:
      "Condensation feeds duplicate copies of the same Pal into the Pal Essence Condenser to raise the kept Pal's star rank. Each rank costs more duplicates than the last, so the cheap early stars come from ordinary catching while the final star is the one worth covering with a Ripe Starfruit instead.",
    steps: [
      ["Build the Pal Essence Condenser", "Unlock it on the technology tree and place it in a base with spare space — condensing is a station action, not a menu action."],
      ["Pick the single Pal you are keeping", "The Pal you place in the keep slot inherits the stars. Its passives and IVs are the ones you live with, so choose the best-rolled copy before you burn duplicates."],
      ["Farm duplicates of the same species", "Only the same Pal counts. Alphas, bred children and caught wild copies all feed the same rank as long as the species matches."],
      ["Condense the cheap early ranks", "The first stars need the fewest copies, so clear them with duplicates you can catch in an afternoon rather than with finite fruits."],
      ["Cover the last rank with a Starfruit", "The final star is the most duplicate-hungry step. Spend a Ripe Starfruit there and keep the catching time."],
    ],
    data: [
      ["What condensing changes", "Star rank only. Level, passives and IVs on the kept Pal are untouched."],
      ["Cost curve", "Duplicate requirements rise with each rank, so later stars cost several times what the first one did."],
      ["Species rule", "Duplicates must be the same Pal. Subspecies and separate species do not substitute for one another."],
      ["Fruit equivalence", "One Starfruit of the matching step replaces that rank's entire duplicate requirement."],
      ["Breeding as a duplicate farm", "A single breeding pair produces same-species eggs on repeat, which is usually faster than hunting wild spawns for the later ranks."],
    ],
    pitfalls: [
      ["Condensing your best-rolled copy away", "Passives live on the individual Pal. Check every copy's passives and pick the keeper before condensing."],
      ["Expecting stars to raise level", "Stars raise stats and work ceilings, not level. You still need to level the Pal normally."],
      ["Feeding mixed species", "Only exact same-species duplicates count; anything else is simply not accepted."],
      ["Grinding wild spawns for the last star", "Breeding a dedicated duplicate pair, or spending a fruit, is far cheaper in real time than farming late-rank duplicates in the field."],
    ],
    faq: [
      ["Do condensed Pals keep their passives?", "Yes — the Pal you keep retains its own passives, level and IVs. The duplicates you feed in are consumed."],
      ["Can you condense a bred Pal?", "Yes. Bred children count as duplicates for their species, which is why a single breeding pair is the standard duplicate farm."],
      ["Is condensing or Starfruit better?", "Whichever resource you have spare. Condense when duplicates are easy to farm; use fruits for rare Pals and for the expensive final rank."],
      ["Does condensation improve work speed?", "It raises the ceiling on work suitability levels, so a starred base worker can out-work an unstarred copy of the same Pal."],
      ["Can you undo condensation?", "No. Fed duplicates are gone, so confirm the keeper Pal's passives before you start."],
    ],
  },
  {
    slug: "palworld-bounty-tokens",
    title: "Palworld Bounty Tokens",
    desc: "What Bounty Tokens buy in Palworld, how to farm them efficiently, and which exchange rewards — Starfruits, stat fruits, schematics — are worth spending them on first.",
    keywords:
      "palworld bounty tokens, palworld bounty token farming, palworld token exchange rewards, palworld bounty board, best bounty token rewards palworld",
    hub: "/map",
    hubLabel: "Palworld interactive map",
    tag: "Progression",
    question: "What are Bounty Tokens used for in Palworld?",
    answer:
      "Bounty Tokens are the exchange currency you earn from bounty and challenge content, and they are the practical source of progression consumables such as Starfruits and stat fruits. Because the exchange stock is limited per cycle, the tokens are worth more than the raw gold value of anything you spend them on.",
    steps: [
      ["Read the current bounty list before you travel", "Take the bounties that overlap on one island so a single run clears several at once instead of fast-travelling between three biomes."],
      ["Chain targets along one route", "Plan a loop that ends near a fast-travel point so the return trip is free. Route efficiency, not damage, is what caps token income."],
      ["Bring capture gear, not only weapons", "Many high-value objectives pay more for a live capture than a kill, and the captured Pal doubles as a condensation duplicate."],
      ["Spend on consumables you cannot farm", "Starfruits and stat fruits are the standout buys because there is no field substitute for them; ammo and materials you can craft are the worst use of tokens."],
      ["Bank a reserve before a rank push", "Decide the Pal you are ranking first, then save tokens until you can buy the whole step at once instead of dribbling them away."],
    ],
    data: [
      ["Where tokens come from", "Bounty and challenge objectives rather than ordinary Pal drops."],
      ["What they buy", "Progression consumables including Starfruits and stat fruits, plus rotating exchange stock."],
      ["Best-value buys", "Anything with no crafting or farming alternative — fruits first, craftables last."],
      ["Route efficiency", "Overlapping objectives on one island beat single high-value targets scattered across the map."],
      ["Version sensitivity", "Exchange stock and payouts have been retuned across patches, so verify current stock in your build before planning a long farm."],
    ],
    pitfalls: [
      ["Spending tokens on craftable items", "If you can build it at a bench, it is not worth a limited currency."],
      ["Killing capture-value targets", "A dead objective can pay less and leaves you without a duplicate for condensing."],
      ["Chasing bounties one at a time", "Travel time dominates. Cluster objectives geographically."],
      ["Planning against outdated payouts", "Token rewards have shifted between versions — check the in-game exchange rather than an old guide table."],
    ],
    faq: [
      ["What is the fastest Bounty Token farm?", "A single-island loop that clears several overlapping objectives and ends near a fast-travel point. Travel time, not combat time, is the bottleneck."],
      ["Can you buy Starfruit with Bounty Tokens?", "Starfruits appear as token exchange rewards rather than merchant stock, so tokens are the practical route to them. Confirm the current stock in your version."],
      ["Do Bounty Tokens expire?", "They persist in your inventory, but exchange stock rotates, so hold tokens with a target purchase in mind rather than indefinitely."],
      ["Are bounties repeatable?", "Bounty content refreshes, which is what makes tokens a farmable currency instead of a one-off reward."],
      ["Should you spend tokens or gold first?", "Spend gold on anything a merchant sells and tokens only on exchange-exclusive items — the two currencies are not interchangeable in value."],
    ],
  },
  {
    slug: "palworld-stat-fruits",
    title: "Palworld Stat Fruits",
    desc: "How Palworld stat fruits raise a Pal's attack, defence, HP and work speed, how they interact with IVs and star ranks, and which Pals actually deserve them.",
    keywords:
      "palworld stat fruits, palworld attack fruit, palworld defense fruit, palworld hp fruit, palworld work speed fruit, best pals for stat fruits",
    hub: "/items",
    hubLabel: "Palworld item database",
    tag: "Items",
    question: "What do stat fruits do in Palworld?",
    answer:
      "Stat fruits permanently raise one stat on the Pal that eats them — attack, defence, HP or work speed — on top of whatever that Pal's level, IVs and star rank already give. They stack with condensation rather than replacing it, so a fully invested endgame Pal uses both.",
    steps: [
      ["Pick a stat that matches the Pal's job", "Attack fruits on a boss-damage carry, defence and HP on a tanky mount you ride into raids, work speed on the base worker you never rotate out."],
      ["Check the Pal's IVs before investing", "Fruits are finite. A poorly rolled copy of the same species wastes them — breed or catch a better base first."],
      ["Rank the Pal before or after, not instead", "Stars and fruits are additive. Neither substitutes for the other, so the strongest Pals get both."],
      ["Feed fruits one stat at a time", "Concentrating on the stat that decides the fight beats spreading fruits thinly across four stats."],
      ["Re-check after a patch", "Stat tuning has changed across versions; verify current values in-game before committing a large stack."],
    ],
    data: [
      ["Attack fruit", "Raises damage output — the highest-value fruit for tower and raid damage checks."],
      ["Defence fruit", "Reduces incoming damage; most useful on the Pal you ride or leave out front."],
      ["HP fruit", "Raises the effective health pool, which matters more than defence against burst damage."],
      ["Work speed fruit", "Raises base work throughput, which is why base-only Pals are still worth investing in."],
      ["Interaction", "Fruits stack with level, IVs and star rank instead of overriding any of them."],
    ],
    pitfalls: [
      ["Feeding fruits to a low-IV Pal", "The base roll is permanent. Fix the base Pal by breeding before you spend finite fruits on it."],
      ["Spreading fruits across every stat", "One decisive stat beats four marginal ones. Pick the stat that decides the encounter."],
      ["Assuming fruits transfer through breeding", "They do not. A bred child starts clean, so fruits are endgame investment on the Pal you keep."],
      ["Ignoring work speed Pals", "Base throughput compounds over hours of play; a work-speed investment often out-earns a fifth attack fruit."],
    ],
    faq: [
      ["Are stat fruits permanent?", "Yes — the increase stays on that Pal for good, which is also why they cannot be reclaimed if you later replace the Pal."],
      ["Do stat fruits stack with condensation?", "Yes. Star ranks and fruits are separate additive layers, so a fully built Pal uses both."],
      ["Do bred children inherit stat fruits?", "No. Fruits are not inherited, only IVs and passives are in play at hatch."],
      ["Which stat fruit is best?", "Attack for damage checks, HP for survival in raids, work speed for base output — the best fruit depends on the Pal's job, not on a global ranking."],
      ["Where do stat fruits come from?", "Token exchange rewards tied to bounty and challenge content, which makes Bounty Token routing the real constraint on how many you own."],
    ],
  },
  {
    slug: "palworld-skill-fruit-trees",
    title: "Palworld Skill Fruit Trees",
    desc: "How Skill Fruit trees work in Palworld, which fruits are worth harvesting, and how to teach an active skill without wasting a rare fruit on the wrong Pal.",
    keywords:
      "palworld skill fruit, palworld skill fruit tree locations, how to teach skills palworld, palworld best skill fruits, palworld skill fruit farming",
    hub: "/map",
    hubLabel: "Palworld interactive map",
    tag: "Map",
    question: "How do Skill Fruit trees work in Palworld?",
    answer:
      "Skill Fruit trees are fixed world nodes that grow one active skill each and regrow on a timer, so the same tree reliably farms the same skill. Feeding a fruit to a Pal teaches that active skill immediately, replacing one of its four skill slots.",
    steps: [
      ["Learn the fixed tree locations", "Trees respawn in place, so a short circuit of three or four known trees is a repeatable farm rather than a random search."],
      ["Match the element to the Pal", "A Pal deals more with skills matching its own element, so an off-element fruit is usually a wasted slot."],
      ["Check what you are overwriting", "Teaching a skill replaces an existing slot. Note the four current skills before you feed the fruit."],
      ["Prioritise high-value coverage skills", "One strong off-element skill can cover a matchup your team otherwise loses, which sometimes beats a same-element upgrade."],
      ["Run the circuit on a regrow timer", "Come back after the regrow window instead of standing at one tree — the loop is what makes the farm efficient."],
    ],
    data: [
      ["Tree behaviour", "Fixed world locations, one skill per tree, regrowing on a timer."],
      ["Teaching effect", "Immediately grants the active skill and overwrites one of four skill slots."],
      ["Element matching", "Same-element skills benefit from the Pal's elemental bonus; off-element skills are for coverage only."],
      ["Slot limit", "Four active skills. Every new fruit is a trade, not an addition."],
      ["Farming pattern", "A three-to-four tree circuit run on the regrow window outperforms camping a single tree."],
    ],
    pitfalls: [
      ["Overwriting your best skill", "The prompt moves fast. Read the four current skills before confirming."],
      ["Feeding off-element fruits by habit", "Without the elemental bonus, the same fruit does noticeably less on the wrong Pal."],
      ["Hunting trees at random", "Trees are fixed. Use the map instead of exploring blind."],
      ["Standing at one tree", "Regrow timers reward a circuit; a single tree idles most of your session."],
    ],
    faq: [
      ["Do Skill Fruit trees respawn?", "Yes — they regrow in place on a timer, which is what makes a fixed circuit of known trees a reliable farm."],
      ["Can you remove a taught skill?", "Not directly; you overwrite it by teaching another skill into that slot, so plan the four-slot loadout before you feed a rare fruit."],
      ["Should you teach off-element skills?", "Only for coverage. Same-element skills gain the elemental bonus, so off-element picks need a matchup reason."],
      ["Do skill fruits work on any Pal?", "Any Pal can learn a skill from a fruit, but the damage payoff depends on the Pal's element and stats."],
      ["Where are the best Skill Fruit trees?", "Cluster them by island and run the shortest loop that ends at a fast-travel point — the interactive map is the fastest way to plan that route."],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Work through this with the ${t.hubLabel} open at ${t.hub}, so every number you act on is the live one.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What the game actually rewards", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${t.hubLabel}`, text: `${hubSentence} Apply the filters described below and work from the tool's output rather than a remembered value.` },
      ],
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The exact order to follow. Each step removes a mistake that costs real playtime.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(4),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `The values and definitions behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "Four failure modes that account for most wasted resources on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions players ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(0),
    },
  ];
}

export const STARFRUIT_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} Guide & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-09-02",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each progression cluster supports, for internal-link rendering. */
export const STARFRUIT_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
